import "./AIAnswerBox.css";
import type {
  AiOverviewData,
  AiSource,
} from "./AiOverview";

interface Section {
  title?: string;
  content?: string;
}

export function renderAIAnswerBox(
  container: HTMLElement,
  data: Partial<AiOverviewData> | null,
  isLoading: boolean = false,
  error: string | null = null,
) {
  if (isLoading) {
    container.innerHTML = `
      <div class="ai-box loading">
        <p class="ai-overview-text">Thinking…</p>
      </div>
    `;
    return;
  }

  if (error) {
    container.innerHTML = `
      <div class="ai-box">
        <p class="ai-overview-text">AI Overview unavailable.</p>
      </div>
    `;
    return;
  }

  if (!data) {
    container.innerHTML = "";
    return;
  }

  const rawOverview = data.overview as unknown;
  let answer = "";

  if (typeof rawOverview === "string") {
    answer = rawOverview;
  } else if (rawOverview && typeof rawOverview === "object") {
    const objectOverview = rawOverview as {
      overview?: string;
      summary?: string;
      content?: unknown;
    };

    answer =
      objectOverview.overview ||
      objectOverview.summary ||
      (Array.isArray(objectOverview.content)
        ? (objectOverview.content as string[]).join(" ")
        : "");
  }

  const facts = Array.isArray(data.facts)
    ? data.facts
    : [];

  const sections = Array.isArray(data.sections)
    ? (data.sections as Section[])
    : [];

  const sources = Array.isArray(data.sources)
    ? data.sources.filter(isUsableSource)
    : [];

  container.innerHTML = `
    <div class="ai-box">
      <h2 class="ai-title">AI Answer</h2>

      ${answer ? `<p class="ai-overview-text">${escapeHtml(answer)}</p>` : ""}

      ${
        facts.length
          ? `
            <ul class="ai-answer-facts">
              ${facts.map((fact) => `<li>${escapeHtml(fact)}</li>`).join("")}
            </ul>
          `
          : ""
      }

      ${
        sections.length
          ? `
            <div class="ai-answer-sections">
              ${sections
                .map(
                  (section) => `
                    <div class="ai-answer-section">
                      ${
                        section.title
                          ? `<strong>${escapeHtml(section.title)}</strong>`
                          : ""
                      }
                      ${
                        section.content
                          ? `<p>${escapeHtml(section.content)}</p>`
                          : ""
                      }
                    </div>
                  `,
                )
                .join("")}
            </div>
          `
          : ""
      }

      ${
        sources.length > 0
          ? `
            <div class="ai-sources">
              <strong>Sources:</strong>
              <ul>
                ${sources.map(renderSource).join("")}
              </ul>
            </div>
          `
          : ""
      }
    </div>
  `;
}

function isUsableSource(source: AiSource | string): boolean {
  if (typeof source === "string") {
    return false;
  }

  if (!source?.url) return false;

  try {
    const parsed = new URL(source.url);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();

    if (!["http:", "https:"].includes(parsed.protocol)) return false;
    if (host === "exa.ai" || host.endsWith(".exa.ai")) return false;

    return true;
  } catch {
    return false;
  }
}

function renderSource(source: AiSource | string): string {
  if (typeof source === "string") {
    return "";
  }

  const label =
    source.title ||
    source.domain ||
    source.url ||
    "Source";

  if (!source.url) {
    return "";
  }

  return `
    <li>
      <a
        href="${escapeHtml(source.url)}"
        target="_blank"
        rel="noopener noreferrer"
      >${escapeHtml(label)}</a>
    </li>
  `;
}

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
